var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) if (e.indexOf(p[i]) < 0)
            t[p[i]] = s[p[i]];
    return t;
};
import { NgZone } from '@angular/core';
import { leaveZone } from '@freelancer/time-utils';
import { isDefined } from '@freelancer/utils';
import { Actions, Effect } from '@ngrx/effects';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import * as Rx from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, mapTo, mergeAll, mergeMap, publishReplay, refCount, startWith, switchMap, tap, withLatestFrom, } from 'rxjs/operators';
import { isRequestDataAction, } from '../actions';
import { StoreBackend } from '../backend';
import { chunk, flatMap, isObject } from '../helpers';
import { slidingBufferTime } from '../operators/slidingBufferTime';
import { RequestStatusHandler, } from '../request-status-handler.service';
import { isDocumentRef, isPlainDocumentRef, referencesEqual, } from '../store.helpers';
import { batchRequests } from './operators/batchRequests';
import { dedupeRequests } from './operators/dedupeRequests';
import { retryRequests } from './operators/retryRequests';
import { RequestDataConfig } from './request-data.interface';
var RequestDataEffect = /** @class */ (function () {
    function RequestDataEffect(storeBackend, actions$, ngZone, requestStatusHandler, config) {
        var _this = this;
        this.storeBackend = storeBackend;
        this.actions$ = actions$;
        this.ngZone = ngZone;
        this.requestStatusHandler = requestStatusHandler;
        this.config = config;
        this.ORIGINAL_REQUESTS_WINDOW = 10000;
        var _a = this.config, scheduler = _a.scheduler, dedupeWindowTime = _a.dedupeWindowTime, batchWindowTime = _a.batchWindowTime, retryConfig = _a.retryConfig;
        var initialInterval = retryConfig.initialInterval, maxRetries = retryConfig.maxRetries, intervalFn = retryConfig.intervalFn;
        // this is a bit of a hack, but not leaving the NgZone when the
        // dedupeWindowTime is null enables the unit tests to work
        var dedupeScheduler = dedupeWindowTime
            ? leaveZone(this.ngZone, scheduler)
            : scheduler;
        var originalRequestsMap = {};
        // Optimisation: dedupe requests with limit smaller than the largest in window
        var allRequests$ = this.actions$.pipe(filter(isRequestDataAction), map(function (action) { return action.payload; }));
        var requestErrors$ = this.requestStatusHandler.statusStream$.pipe(filter(function (_a) {
            var statusObject = _a.statusObject;
            return statusObject.error !== undefined;
        }));
        var propagateDedupedRequestStatuses$ = requestErrors$.pipe(withLatestFrom(Rx.combineLatest([
            // sliding buffer of failed requests clientRequestIds
            requestErrors$.pipe(map(function (_a) {
                var request = _a.request;
                return request;
            }), slidingBufferTime(dedupeWindowTime, dedupeScheduler), map(function (requestIdErrorBuffer) {
                return flatMap(requestIdErrorBuffer, function (_a) {
                    var clientRequestIds = _a.clientRequestIds;
                    return clientRequestIds;
                });
            })),
            // sliding buffer of all requests
            allRequests$.pipe(slidingBufferTime(dedupeWindowTime, dedupeWindowTime ? leaveZone(this.ngZone, scheduler) : undefined)),
        ])), tap(function (_a) {
            var _b = _a[0], request = _b.request, statusObject = _b.statusObject, _c = _a[1], requestIdErrorBuffer = _c[0], requestBuffer = _c[1];
            // updates statuses of deduped requests so that the first request's
            // status is propagated to subsequent deduped ones
            requestBuffer
                .filter(function (r) {
                // has a previous request (r) been made to the same ref
                return referencesEqual(r.ref, request.ref) &&
                    // has none of the current request (request) clientRequestIds
                    // resulted in a previous error
                    r.clientRequestIds.find(function (id) {
                        return requestIdErrorBuffer.includes(id);
                    }) === undefined;
            })
                .forEach(function (r) { return _this.requestStatusHandler.update(r, statusObject); });
        }), mapTo(undefined), startWith(undefined));
        var allDedupedRequests$ = Rx.combineLatest([
            allRequests$.pipe(dedupeRequests(dedupeWindowTime, requestErrors$.pipe(map(function (e) { return e.request; })), dedupeScheduler)),
            propagateDedupedRequestStatuses$,
        ]).pipe(map(function (_a) {
            var request = _a[0];
            return request;
        }), tap(function (request) {
            var _a;
            originalRequestsMap = __assign({}, originalRequestsMap, (_a = {}, _a[request.clientRequestIds[0]] = request, _a));
            // remove request after window elapses
            dedupeScheduler.schedule(function () {
                var _a = request.clientRequestIds[0], _deleted = originalRequestsMap[_a], cleanedOriginalRequestsMap = __rest(originalRequestsMap, [typeof _a === "symbol" ? _a : _a + ""]);
                originalRequestsMap = cleanedOriginalRequestsMap;
            }, _this.ORIGINAL_REQUESTS_WINDOW);
        }), distinctUntilChanged(), publishReplay(1), refCount());
        var plainRequests$ = allDedupedRequests$.pipe(filter(function (request) { return isPlainDocumentRef(request.ref); }));
        var queriedRequests$ = allDedupedRequests$.pipe(filter(function (request) { return !isPlainDocumentRef(request.ref); }));
        // Batching only for plain requests (no query)
        // TODO: Address queried requests, i.e. regular datastore.list() calls
        var batchedRequests$ = plainRequests$.pipe(batchRequests(batchWindowTime, scheduler), map(function (requests) {
            return flatMap(requests, function (request) {
                return splitRequestsOnId(request, _this.storeBackend.batchSize(request.ref));
            });
        }), mergeAll());
        var response$ = Rx.merge(batchedRequests$, queriedRequests$).pipe(mergeMap(function (request) {
            return _this.storeBackend.fetch(request.ref).pipe(map(function (response) {
                if (request.clientRequestIds.length > 1) {
                    var unbatchedRequests = request.clientRequestIds
                        .map(function (id) { return originalRequestsMap[id]; })
                        .filter(isDefined);
                    return { res: response, unbatchedRequests: unbatchedRequests };
                }
                return { res: response, unbatchedRequests: [request] };
            }), map(function (_a) {
                var res = _a.res, unbatchedRequests = _a.unbatchedRequests;
                return _this.checkEmptyResponse(res, request, unbatchedRequests);
            }), switchMap(function (_a) {
                var res = _a.res, otherRequests = _a.otherRequests, requestsWithMissingItems = _a.requestsWithMissingItems;
                if (requestsWithMissingItems.length > 0) {
                    return Rx.throwError({
                        res: res,
                        otherRequests: otherRequests,
                        requestsWithMissingItems: requestsWithMissingItems,
                    });
                }
                // No missing items - dispatch a success action with the original,
                // possibly batched request
                return Rx.of([{ request: request, res: res }]);
            }), 
            // On missing items, retry twice with exponential backoff
            retryRequests({
                initialInterval: initialInterval,
                maxRetries: maxRetries,
                intervalFn: intervalFn,
                scheduler: scheduler,
            }), 
            // After all retries fail, produce an error status for the unbatched
            // requests that were missing items
            catchError(function (err) {
                var res = err.res, otherRequests = err.otherRequests, requestsWithMissingItems = err.requestsWithMissingItems;
                // Only handle the error thrown by missing item logic above
                if (res && otherRequests && requestsWithMissingItems) {
                    // TODO: replace with ErrorHandler
                    console.warn("Object(s) not found in response from document call to '" + request.type + "'. Response was", res);
                    return Rx.of((otherRequests || []).map(function (req) { return ({
                        request: req,
                        res: res,
                    }); }).concat((requestsWithMissingItems || []).map(function (req) { return ({
                        request: req,
                        res: {
                            status: 'error',
                            errorCode: ErrorCodeApi.NOT_FOUND,
                        },
                    }); })));
                }
                // Rethrow other errors - mainly here to so that the missing module
                // error is correctly thrown from `storeBackend.fetch`.
                throw err;
            }));
        }));
        this.requestData$ = response$.pipe(tap(function (requests) {
            requests.forEach(function (_a) {
                var request = _a.request, res = _a.res;
                return _this.updateRequestStatuses(res, request);
            });
        }), map(function (requests) {
            return requests.map(function (_a) {
                var request = _a.request, res = _a.res;
                return _this.dispatchResponseAction(res, request);
            });
        }), mergeAll());
    }
    /**
     * Inspects the network response to verify all items expected by the request
     * actually exist.
     *
     * FIXME: This is not a comprehensive check for missing items, as it relies on
     * several assumptions about the structure of the result object. These
     * assumptions often don't hold for our inconsistent API. This problem can also
     * be avoided entirely by not batching requests.
     * TODO: Find a generic way to check if a response is missing items.
     */
    RequestDataEffect.prototype.checkEmptyResponse = function (response, originalRequest, unbatchedRequests) {
        if (isDocumentRef(originalRequest.ref) && response.status === 'success') {
            var _a = this.getRequestsWithMissingItems(response, unbatchedRequests), requestsWithMissingItems = _a.requestsWithMissingItems, otherRequests = _a.otherRequests;
            return {
                res: response,
                requestsWithMissingItems: requestsWithMissingItems,
                otherRequests: otherRequests,
            };
        }
        return {
            res: response,
            requestsWithMissingItems: [],
            otherRequests: unbatchedRequests,
        };
    };
    /**
     * Partitions requests into ones that are missing items, and ones that are not.
     */
    RequestDataEffect.prototype.getRequestsWithMissingItems = function (response, unbatchedRequests) {
        var result = response.result; // FIXME
        var keys = isObject(result) ? Object.keys(result) : [];
        // Check if result is a single key with an empty Array or Object value
        if (keys.length === 1) {
            var resultItems = result[keys[0]];
            if (resultItems &&
                ((Array.isArray(resultItems) && resultItems.length === 0) ||
                    (isObject(resultItems) && Object.keys(resultItems).length === 0))) {
                return {
                    requestsWithMissingItems: unbatchedRequests,
                    otherRequests: [],
                };
            }
        }
        // Assume all other result structures are fine
        return { requestsWithMissingItems: [], otherRequests: unbatchedRequests };
    };
    RequestDataEffect.prototype.updateRequestStatuses = function (response, request) {
        if (response.status === 'error') {
            this.requestStatusHandler.update(request, {
                ready: false,
                error: response,
            });
        }
    };
    RequestDataEffect.prototype.dispatchResponseAction = function (response, request) {
        var order = request.ref.order ||
            this.storeBackend.defaultOrder(request.ref.path.collection);
        switch (response.status) {
            case 'success': {
                var action = {
                    type: 'API_FETCH_SUCCESS',
                    payload: {
                        type: request.ref.path.collection,
                        result: response.result,
                        ref: request.ref,
                        order: order,
                        clientRequestIds: request.clientRequestIds,
                    },
                };
                return action;
            }
            default: {
                var action = {
                    type: 'API_FETCH_ERROR',
                    payload: {
                        type: request.ref.path.collection,
                        ref: request.ref,
                        order: order,
                        clientRequestIds: request.clientRequestIds,
                    },
                };
                return action;
            }
        }
    };
    __decorate([
        Effect(),
        __metadata("design:type", Rx.Observable)
    ], RequestDataEffect.prototype, "requestData$", void 0);
    return RequestDataEffect;
}());
export { RequestDataEffect };
function splitRequestsOnId(request, size) {
    var ids = request.ref.path.ids;
    if (!ids) {
        return [request];
    }
    return chunk(ids, size).map(function (chunkedIds) { return (__assign({}, request, { ref: __assign({}, request.ref, { path: __assign({}, request.ref.path, { ids: chunkedIds }) }) })); });
}
